import type { FieldSchema, ShowIf } from "@a2z/storefront-schemas";

/**
 * `showIf` evaluation for the admin inspector.
 *
 * **Why this is not imported from the seller editor, and not copied from it.**
 * The seller's equivalent lives at
 * `apps/seller/src/modules/store-editor/components/SchemaForm/conditional.ts`.
 * It is app-local, so admin cannot import it, and this codebase has been bitten
 * hard by hand-maintained mirrors (two `heroVariantThumbs` copies drifted to
 * 131 vs 93 glyphs and every ported variant drew the wrong sketch). So this is
 * NOT a copy of that file — it is ~30 lines written directly against the shared
 * `ShowIf` type, which is where the semantics actually live.
 *
 * That distinction is the whole point: if `ShowIf` ever gains an operator, both
 * implementations stop compiling rather than silently disagreeing. Two glyph
 * tables can both type-check while drawing different pictures; two readers of
 * the same discriminated type cannot.
 *
 * The extraction that deletes this file is specified in
 * `docs/plans/2026-07-20-admin-theme-studio.md` §2 — it moves the seller's
 * `conditional.ts` into a new `packages/storefront-controls` and this module
 * becomes a one-line re-export. Deliberately not done here: it edits
 * `apps/seller/**`, owned by the in-flight 13-family workflow.
 */

/**
 * Semantics, matching the `ShowIf` doc comment in the shared types:
 * a scalar requires strict equality; an array matches on membership; multiple
 * keys are AND-ed.
 */
export function evaluateShowIf(
  showIf: ShowIf | undefined,
  values: Record<string, unknown>,
): boolean {
  if (!showIf) return true;
  return Object.entries(showIf).every(([key, expected]) => {
    const actual = values[key];
    if (Array.isArray(expected)) {
      return (expected as Array<string | number>).some((v) => v === actual);
    }
    return actual === expected;
  });
}

/**
 * Overlay a section's stored settings on its schema defaults, so `showIf` is
 * evaluated against what the storefront will actually render rather than
 * against the sparse stored object. A field the author never touched still
 * gates correctly on its default.
 */
export function withFieldDefaults(
  fields: readonly FieldSchema[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of fields) {
    if ("default" in field && field.default !== undefined) {
      out[field.id] = field.default;
    }
  }
  return { ...out, ...values };
}
